import React, { useEffect, useState } from "react";
import Button from "../../../ui/Button";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import {
  fetchGeneralSettings,
  updateGeneralSettingsThunk,
} from "../../../../redux/features/settingsSlice"; // Update this path
import {
  convertFromSeconds,
  convertToSeconds,
  convertToUnit,
} from "../../../../helpers";
import DurationInputWithUnit from "../../../ui/DurationInput";

interface EditGeneralSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

const EditGeneralSettingsModal: React.FC<EditGeneralSettingsModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { loading, general } = useAppSelector(
    (state: RootState) => state.settings
  );

  const [durationFields, setDurationFields] = useState({
    paymentDuration: { value: 1, unit: "days" },
    disputeAppealDuration: { value: 2, unit: "days" },
    acceptOrderDuration: { value: 2, unit: "days" },
    requestReturnDuration: { value: 3, unit: "days" },
    dispatchOrderWaitingDuration: { value: 1, unit: "days" },
    acceptInitialOrderDuration: { value: 1, unit: "days" },
  });

  useEffect(() => {
    if (general) {
      setDurationFields({
        paymentDuration: convertToUnit(general.paymentDuration),
        disputeAppealDuration: convertToUnit(general.disputeAppealDuration),
        acceptOrderDuration: convertToUnit(general.acceptOrderDuration),
        requestReturnDuration: convertToUnit(general.requestReturnDuration),
        dispatchOrderWaitingDuration: convertToUnit(
          general.dispatchOrderWaitingDuration
        ),
        acceptInitialOrderDuration: convertToUnit(
          general.acceptInitialOrderDuration
        ),
      });
    }
  }, [general]);

  const handleDurationChange = (
    field: string,
    newValue: number,
    newUnit: string
  ) => {
    setDurationFields((prev: any) => {
      const { value: currentValue, unit: currentUnit } = prev[field];

      let valueToUse = newValue;

      // If the unit has changed
      if (newUnit !== currentUnit) {
        const valueInSeconds = convertToSeconds(currentValue, currentUnit);
        valueToUse = convertFromSeconds(valueInSeconds, newUnit);
      }

      return {
        ...prev,
        [field]: {
          value: valueToUse < 0 ? 0 : valueToUse, // Ensure value is not negative
          unit: newUnit,
        },
      };
    });
  };
  const handleSubmit = async () => {
    const payload = {
      id: general.id,
      status: "ACTIVE",
      paymentDuration: convertToSeconds(
        durationFields.paymentDuration.value,
        durationFields.paymentDuration.unit
      ),
      disputeAppealDuration: convertToSeconds(
        durationFields.disputeAppealDuration.value,
        durationFields.disputeAppealDuration.unit
      ),
      acceptOrderDuration: convertToSeconds(
        durationFields.acceptOrderDuration.value,
        durationFields.acceptOrderDuration.unit
      ),
      requestReturnDuration: convertToSeconds(
        durationFields.requestReturnDuration.value,
        durationFields.requestReturnDuration.unit
      ),
      dispatchOrderWaitingDuration: convertToSeconds(
        durationFields.dispatchOrderWaitingDuration.value,
        durationFields.dispatchOrderWaitingDuration.unit
      ),
      acceptInitialOrderDuration: convertToSeconds(
        durationFields.acceptInitialOrderDuration.value,
        durationFields.acceptInitialOrderDuration.unit
      ),
    };

    try {
      await dispatch(updateGeneralSettingsThunk(payload)).unwrap();
      toast.success("Settings updated successfully");
      dispatch(fetchGeneralSettings()); // Refresh settings after update
      onClose();
    } catch (err) {
      toast.error("Failed to update settings");
      console.error(err);
    }
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit General Settings"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex justify-between">
          <Button
            label="Discard"
            onClick={onClose}
            customStyle={{
              width: "auto",
              minWidth: "150px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            label="Done"
            onClick={handleSubmit}
            isLoading={loading}
            customStyle={{ width: "auto", minWidth: "150px" }}
          />
        </div>
      }
    >
      <div className="p-6 w-full">
        <DurationInputWithUnit
          label="Payment Duration"
          value={durationFields.paymentDuration.value}
          unit={durationFields.paymentDuration.unit}
          onChange={(val, unit) =>
            handleDurationChange("paymentDuration", val, unit)
          }
        />
        <DurationInputWithUnit
          label="Dispute Appeal Duration"
          value={durationFields.disputeAppealDuration.value}
          unit={durationFields.disputeAppealDuration.unit}
          onChange={(val, unit) =>
            handleDurationChange("disputeAppealDuration", val, unit)
          }
        />
        <DurationInputWithUnit
          label="Accept Order Duration"
          value={durationFields.acceptOrderDuration.value}
          unit={durationFields.acceptOrderDuration.unit}
          onChange={(val, unit) =>
            handleDurationChange("acceptOrderDuration", val, unit)
          }
        />
        <DurationInputWithUnit
          label="Request Return Duration"
          value={durationFields.requestReturnDuration.value}
          unit={durationFields.requestReturnDuration.unit}
          onChange={(val, unit) =>
            handleDurationChange("requestReturnDuration", val, unit)
          }
        />
        <DurationInputWithUnit
          label="Dispatch Order Waiting Duration"
          value={durationFields.dispatchOrderWaitingDuration.value}
          unit={durationFields.dispatchOrderWaitingDuration.unit}
          onChange={(val, unit) =>
            handleDurationChange("dispatchOrderWaitingDuration", val, unit)
          }
        />
        <DurationInputWithUnit
          label="Accept Initial Order Duration"
          value={durationFields.acceptInitialOrderDuration.value}
          unit={durationFields.acceptInitialOrderDuration.unit}
          onChange={(val, unit) =>
            handleDurationChange("acceptInitialOrderDuration", val, unit)
          }
        />
      </div>
    </CustomModalContainer>
  );
};

export default EditGeneralSettingsModal;
